import React, { useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ExternalLink } from 'lucide-react';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const cinemaIcon = new L.DivIcon({
  className: 'custom-cinema-marker',
  html: `
    <div style="
      width: 40px;
      height: 40px;
      background: #f5f5dc;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 3px solid #1a2b3c;
      box-shadow: 0 3px 10px rgba(0,0,0,0.4);
    ">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a2b3c" stroke-width="2.5">
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    </div>
  `,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

const selectedCinemaIcon = new L.DivIcon({
  className: 'custom-cinema-marker-selected',
  html: `
    <div style="
      width: 46px;
      height: 46px;
      background: #50c878;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 3px solid #fff;
      box-shadow: 0 0 0 4px rgba(80,200,120,0.4), 0 3px 10px rgba(0,0,0,0.4);
    ">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5">
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    </div>
  `,
  iconSize: [46, 46],
  iconAnchor: [23, 46],
  popupAnchor: [0, -46],
});

const userIcon = new L.DivIcon({
  className: 'custom-user-marker',
  html: `
    <div style="
      width: 24px;
      height: 24px;
      background: #1a2b3c;
      border-radius: 50%;
      border: 4px solid #f5f5dc;
      box-shadow: 0 0 0 8px rgba(243, 222, 181, 0.3), 0 2px 8px rgba(0,0,0,0.3);
    "></div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Handles map view changes and opening popups
const MapController = ({ userLocation, selectedCinema, markerRefs }) => {
  const map = useMap();

  useEffect(() => {
    if (selectedCinema?.coordinates) {
      const { lat, lng } = selectedCinema.coordinates;
      map.flyTo([lat, lng], 15, { duration: 0.8 });

      // Open the popup for the selected cinema after fly animation
      setTimeout(() => {
        const ref = markerRefs.current[selectedCinema.id];
        if (ref) {
          ref.openPopup();
        }
      }, 850);
    }
  }, [selectedCinema, map, markerRefs]);

  // React to userLocation changes (city search)
  useEffect(() => {
    if (userLocation && !selectedCinema) {
      map.flyTo([userLocation.lat, userLocation.lng], 12, { duration: 0.8 });
    }
  }, [userLocation, map, selectedCinema]);

  return null;
};

const CinemaMap = ({ cinemas, userLocation, selectedCinema, onSelectCinema, movieTitle }) => {
  const markerRefs = useRef({});
  const initialCenter = userLocation
    ? [userLocation.lat, userLocation.lng]
    : [40.4168, -3.7038];

  const setMarkerRef = useCallback((cinemaId, ref) => {
    if (ref) {
      markerRefs.current[cinemaId] = ref;
    }
  }, []);

  return (
    <div className="relative w-full h-full" style={{ backgroundColor: '#f5f5dc' }}>
      <MapContainer
        center={initialCenter}
        zoom={12}
        style={{ height: '100%', width: '100%' }}
        className="z-0"
      >
        <MapController
          userLocation={userLocation}
          selectedCinema={selectedCinema}
          markerRefs={markerRefs}
        />
        
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />

        {/* User location marker */}
        {userLocation && (
          <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
            <Popup>
              <div className="text-center p-2">
                <p className="font-semibold text-[#1a2b3c]">Tu ubicacion</p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Cinema markers */}
        {cinemas.map((cinema) => {
          if (!cinema.coordinates?.lat || !cinema.coordinates?.lng) return null;
          const isSelected = selectedCinema?.id === cinema.id;
          return (
            <Marker
              key={cinema.id}
              position={[cinema.coordinates.lat, cinema.coordinates.lng]}
              icon={isSelected ? selectedCinemaIcon : cinemaIcon}
              ref={(ref) => setMarkerRef(cinema.id, ref)}
              eventHandlers={{ click: () => onSelectCinema(cinema) }}
            >
              <Popup>
                <div className="min-w-[220px] p-1">
                  <h3 className="font-bold text-[#1a2b3c] text-base mb-1">{cinema.name}</h3>
                  <p className="text-gray-600 text-sm mb-1">{cinema.address}</p>
                  <p className="text-[#50c878] text-sm mb-2 font-medium">{cinema.formattedDistance} de distancia</p>
                  <p className="text-gray-500 text-xs mb-3">{movieTitle || 'Los Legendarios'}</p>
                  
                  <div className="flex flex-wrap gap-1">
                    {cinema.showtimes?.slice(0, 4).map((showtime) => (
                      <a
                        key={showtime.id}
                        href={cinema.website || cinema.googleMapsUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${
                          showtime.available
                            ? 'bg-[#1a2b3c] text-[#f5f5dc] hover:bg-[#2e4a2a]'
                            : 'bg-gray-200 text-gray-400'
                        }`}
                      >
                        {showtime.time}
                        <span className="opacity-70">({showtime.format})</span>
                        {showtime.available && <ExternalLink className="w-2.5 h-2.5" />}
                      </a>
                    ))}
                    {cinema.showtimes?.length > 4 && (
                      <span className="text-xs text-gray-500 self-center">+{cinema.showtimes.length - 4} mas</span>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      <div className="absolute bottom-4 left-4 bg-[#1a2b3c]/90 backdrop-blur-sm rounded-lg px-3 py-2 z-[1000]">
        <p className="text-[#f5f5dc]/80 text-xs">© OpenStreetMap</p>
      </div>
    </div>
  );
};

export default CinemaMap;
